import { useState } from "react";
import { Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu } from "lucide-react";
import Sidebar from "./sidebar";
import Topbar from "./topbar";
import ErrorBoundary from "./error-boundary";
import HelpDialog from "./help-dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { authApi } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";

export default function AppShell() {
  // Cheap session probe. The api.ts request() helper auto-redirects on 401,
  // so a successful return here means the cookie is good.
  const { isLoading, isError } = useQuery({
    queryKey: ["auth", "ping"],
    queryFn: authApi.ping,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // ? opens help dialog (force = true so it works even when an input is
  // focused — small UX win since you might be mid-typing and forget a chord)
  useKeyboardShortcuts(
    [{ key: "?", shift: true, force: true, handler: () => setHelpOpen(true) }],
    true,
  );

  if (isLoading) return <BootSplash />;
  if (isError) return <AuthFailedSplash />;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-surface">
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" hideClose className="border-0 p-0">
          <Sidebar />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenDrawer={() => setDrawerOpen(true)} onOpenHelp={() => setHelpOpen(true)} />
        <main className="min-h-0 flex-1 overflow-hidden">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
}

function MenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="rounded p-1.5 text-ink-muted hover:bg-surface-hover hover:text-ink md:hidden"
      aria-label="Open menu"
    >
      <Menu className="h-5 w-5" />
    </button>
  );
}
export { MenuButton };

function BootSplash() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-surface">
      <div className="flex flex-col items-center gap-4">
        <div className="grid h-12 w-12 place-items-center rounded-lg bg-gradient-to-br from-brand to-brand-bright">
          <span className="font-display text-base font-bold text-brand-ink">LG</span>
        </div>
        <Skeleton className="h-2 w-32" />
      </div>
    </div>
  );
}

function AuthFailedSplash() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-surface">
      <div className="text-center">
        <p className="font-display text-2xl font-semibold text-ink">Sign in to continue</p>
        <p className="mt-1 text-sm text-ink-muted">Redirecting you to Microsoft 365…</p>
      </div>
    </div>
  );
}
